import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Map as MapIcon, TrendingUp, TrendingDown, Minus, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/app/PageHeader";
import { Loader, EmptyState } from "@/components/app/Loader";
import { MichiganCountyMap, MOOD_HEX } from "@/components/app/MichiganCountyMap";
import { CountyDetailSheet } from "@/components/app/CountyDetailSheet";
import { Badge } from "@/components/ui/badge";

const MOOD_ICON = { positive: TrendingUp, neutral: Minus, negative: TrendingDown };
const countyOf = (u) => (u.counties?.length ? u.counties[0] : (u.geo_unit || "").replace(/\s+County$/i, ""));

export default function SentimentMap() {
  const [county, setCounty] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["geo"], queryFn: () => api.get("/audience/geo").then((r) => r.data) });

  if (isLoading) return <Loader label="Loading sentiment map…" />;
  if (!data?.length) return <EmptyState icon={MapIcon} title="No geographic data" description="Import a voter file to map sentiment against voter density." />;

  const isStatewide = data[0]?.geo_type === "region";
  const scopeLabel = data[0]?.geo_label || (isStatewide ? "Michigan, Statewide" : "Kalamazoo County");

  return (
    <div>
      <PageHeader
        title="Sentiment Map"
        subtitle="Issue sentiment from the listening feed, joined to where your actual voters live. Click any county for its voters, precincts and local coverage."
        icon={MapIcon}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.4fr_1fr]">
        {/* Real county boundaries, shaded by sentiment */}
        <div className="rounded-xl border border-border bg-white p-4 shadow-sm">
          <div className="overflow-hidden rounded-lg border border-border bg-slate-50">
            <MichiganCountyMap units={data} label={scopeLabel} onSelect={setCounty} />
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-5">
            {Object.entries(MOOD_HEX).map(([k, v]) => (
              <div key={k} className="flex items-center gap-1.5 text-xs">
                <span className="h-3 w-3 rounded-full" style={{ background: v }} />
                <span className="capitalize text-brand-mute">{k}</span>
              </div>
            ))}
            <div className="flex items-center gap-1.5 text-xs">
              <span className="h-3 w-3 rounded-full bg-[#e9eef5]" />
              <span className="text-brand-mute">outside the race</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="h-3 w-3 rounded-full bg-brand-navy" />
              <span className="text-brand-mute">voter density</span>
            </div>
          </div>
        </div>

        {/* List */}
        <div className="space-y-3">
          {data.map((d) => {
            const Icon = MOOD_ICON[d.mood];
            return (
              <button key={d.id} type="button" onClick={() => setCounty(countyOf(d))}
                className="w-full rounded-xl border border-border bg-white p-4 text-left shadow-sm transition-colors hover:border-brand-red/40 hover:bg-slate-50"
                data-testid={`geo-card-${d.geo_unit.replace(/\s+/g, "-")}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="flex items-center gap-1 font-heading font-bold text-brand-ink">
                      {d.geo_unit} <ChevronRight className="h-4 w-4 text-brand-mute" />
                    </p>
                    <p className="text-xs capitalize text-brand-mute">
                      {d.geo_type} · {d.voter_count.toLocaleString()} voters ({d.voter_share}%)
                      {d.signal_count !== undefined && <span className="normal-case"> · {d.signal_count} local {d.signal_count === 1 ? "signal" : "signals"}</span>}
                    </p>
                  </div>
                  <span className="flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold capitalize" style={{ background: `${MOOD_HEX[d.mood]}1a`, color: MOOD_HEX[d.mood] }}>
                    <Icon className="h-3.5 w-3.5" /> {d.mood}
                  </span>
                </div>
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {d.top_issues.map((t) => <Badge key={t} variant="outline" className="border-brand-navy/20 bg-brand-navy/[0.04] text-brand-navy">{t}</Badge>)}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <CountyDetailSheet county={county} onClose={() => setCounty(null)} />
    </div>
  );
}
